import { useState, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import useDesignUploads from './useDesignUploads';
import { updateWidgetSetting } from './widgetUtil';
// import { logClear } from '../../../../utils';
import useCSSUpload from './useCSSUpload';
import { useButtonsStore } from '../../../../store/models';
import useUserPublisherInfo from '../../../../store/models/user/useUserPublisherInfo';

function useDesign (props) {
    const {
        dataSource, // dataSource here is the input component
        onDataChange,
        updateSavable,
        setTabDisabled = () => {},
    } = props;
    const buttonsStore = useButtonsStore();
    const widgetRef = useRef();

    const {
        publisherLogo,
    } = useUserPublisherInfo({});

    function decodeSource () {
        const decoded = {
            ...dataSource.style.eventDisplay,
            logo: dataSource.style.logo,
            language: dataSource.style.language,
            cssFile: dataSource.style.cssFile,
            showAllEvents: dataSource.display.showAllEvents,
            selectAllEvents: dataSource.display.selectAllEvents,
        };
        return decoded;
    }

    function encodeSavable (data, newStyles = {}) {
        // Note: dataSource here is the input state created in useModuleInput
        const newData = {
            ...dataSource,
            style: {
                ...dataSource.style,
                ...newStyles,
                language: data.language,
                logo: data.logo,
                cssFile: data.cssFile,
                eventDisplay: {
                    ...data,
                    cssFile: undefined,
                    logo: undefined,
                    language: undefined,
                    showAllEvents: undefined,
                    selectAllEvents: undefined,
                },
            },

            display: {
                ...dataSource.display,
                showAllEvents: data.showAllEvents,
                selectAllEvents: data.selectAllEvents,
            },
        };

        return newData;
    }

    function getInitialState () {
        return decodeSource();
    }

    function selectLogo (style) {
        if (style.logo.includes('v4/publishers/logo/image')) {
            style.logo = publisherLogo;
        } else if (style.logo.includes('cropped-ecal-logo.png')) {
            // also handled the legacy cropped-ecal-logo that is not existing
            style.logo = publisherLogo;
        }
    }

    function styleSource () {
        const style = buttonsStore.stylesData || dataSource.style;
        const newStyle = {
            ...style,
            /* new Props */
            logoShadow: style.logoShadow,
            logoTransparent: style.logoTransparent,
            logoShape: style.logoShape,
            bannerURL: style.bannerURL,
            bannerType: style.bannerType,
            bannerColour: style.bannerColour,
        };
        selectLogo(newStyle);
        return newStyle;
    }

    const [designData, setDesignDataState] = useState(getInitialState());
    const [styleData, setStyleDataState] = useState(styleSource());

    useEffect(() => {
        // only update editor state after stylesData update
        buttonsStore.reflectEditorStateUpdates();
    }, [styleData]);

    function updateWidgetPreview (settings) {
        // logClear();
        if (widgetRef.current) {
            updateWidgetSetting(settings);
        }
    }

    function initWidgetPreview () {
        updateWidgetPreview(dataSource);
    }

    useEffect(() => {
        buttonsStore.setStyleSetter(setStyleData);
        buttonsStore.setStylesDataUpdater(setStyleState);
        if (isEmpty(buttonsStore.stylesData)) {
            buttonsStore.stylesData = { ...dataSource.style };
        }
        window.onEventWidgetLaunched = () => {
            setTimeout(initWidgetPreview, 1000);
        };

        return () => {
            if (typeof onDataChange === 'function') {
                onDataChange();
            }
        };
    }, []);

    useEffect(() => {
        setDesignDataState(decodeSource());
        updateWidgetPreview(dataSource);
        setStyleDataState(styleSource());
    }, [dataSource]);

    useEffect(() => {
        if (widgetRef.current) {
            setTimeout(initWidgetPreview, 1000);
        }
    }, [widgetRef.current]);

    function setDesignData (data) {
        setDesignDataState(data);
        const encoded = encodeSavable({
            ...data,
        },
        {
            ...buttonsStore.stylesData,
            language: data.language,
            logo: data.logo,
            cssFile: data.cssFile,
        });
        updateWidgetPreview(encoded);
        updateSavable(encoded);
    }

    function setStyleState () {
        setStyleDataState({
            ...buttonsStore.stylesData,
        });
    }

    function setStyleData (newStyle) {
        setStyleDataState({
            ...buttonsStore.stylesData,
            ...newStyle,
        });

        const data = { ...designData };
        if (Object.prototype.hasOwnProperty.call(newStyle, 'logo')) {
            // captures  newStyle if it has logo properties regardless of its value = "" or a url
            // update logo and DesignData state so when encodeSavable is call, logo will be updated.
            data.logo = newStyle.logo;
            setDesignDataState({
                ...data,
            });
        }

        const encoded = encodeSavable(
            data,
            {
                ...buttonsStore.stylesData,
                ...newStyle,
            }
        );

        updateWidgetPreview(encoded);
        updateSavable(encoded);
    }

    const {
        uploadCSSFile,
        cssFile,
        cssFileError,
    } = useCSSUpload({
        dataSource,
        designData,
        setDesignData,
    });

    const {
        buttonLogo,
        uploadButtonLogo,
        deleteButtonLogo,
        buttonLogoError,
    } = useDesignUploads({
        designData,
        setDesignData,
        setTabDisabled,
    });

    return {
        widgetRef,
        designData,
        setDesignData,
        buttonLogo,
        uploadButtonLogo,
        deleteButtonLogo,
        updateWidgetPreview,
        buttonLogoError,

        uploadCSSFile,
        cssFile,
        cssFileError,
        setStyleData,
        styleData,
    };
}

export default useDesign;
